import { Autocomplete } from "@base-ui/react/autocomplete";
import { LIMITS } from "@listou/shared/constants";
import { catalogueStickerUrl } from "@listou/shared/stickers";
import type { PublicProduct } from "@listou/shared/types/api";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import {
  CatalogueResults,
  OccasionFilter,
  SortSelect,
  validateCatalogueSearch,
  type CatalogueSearch,
} from "../components/catalogue";
import { GuideShelves } from "../components/gift-guide";
import { Sticker } from "../components/list-page/parts";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { formatRating } from "../components/stars";
import { GIFT_GUIDE_PAGES } from "../lib/gift-guides";
import { seo } from "../lib/head";
import { catalogueCategoriesQuery, catalogueQuery } from "../lib/queries";
import { viewerQuery } from "../lib/session";

export const Route = createFileRoute("/idees-cadeaux")({
  validateSearch: validateCatalogueSearch,
  staticData: { ssr: true },
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps }) =>
    Promise.all([
      queryClient.prefetchInfiniteQuery(catalogueQuery(deps)),
      queryClient.prefetchQuery(catalogueCategoriesQuery("birth")),
    ]),
  // Filtering reloads this same page: it keeps the search box and the current results on
  // screen until the new ones arrive, instead of swapping the whole page for a spinner.
  pendingMs: Number.POSITIVE_INFINITY,
  head: () =>
    seo({
      title: "Idées cadeaux testées par les parents · Listou",
      description:
        "Les cadeaux que les parents ajoutent à leurs listes et recommandent : naissance, Noël, anniversaire. Notes et avis vérifiés.",
    }),
  component: CataloguePage,
});

function CataloguePage() {
  const { q, occasion, sort = "popular" } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const viewer = useQuery({ ...viewerQuery, retry: false });
  const [text, setText] = useState(q ?? "");

  // Typing updates the URL once it pauses, which is what drives the query.
  useEffect(() => {
    const next = text.trim() || undefined;
    if (next === q) return;
    const timer = setTimeout(
      () =>
        void navigate({ search: (prev: CatalogueSearch) => ({ ...prev, q: next }), replace: true }),
      300,
    );
    return () => clearTimeout(timer);
  }, [text, q, navigate]);

  const catalogue = useInfiniteQuery(catalogueQuery({ q, occasion, sort }));
  const categories = useQuery(catalogueCategoriesQuery("birth"));
  const products = catalogue.data?.pages.flatMap((page) => page.products) ?? [];

  return (
    <div className="min-h-dvh">
      <SiteHeader signedIn={viewer.isSuccess} />
      <main className="mx-auto grid max-w-6xl gap-8 px-4 pt-6 pb-20 sm:px-6">
        <header className="grid items-center gap-6 sm:grid-cols-[1fr_auto]">
          <div className="grid max-w-3xl gap-3">
            <h1 className="text-5xl font-medium sm:text-6xl">
              Idées cadeaux testées par les parents
            </h1>
            <p className="text-xl text-ink/80">
              Ce que les parents mettent sur leurs listes, et ce qu'ils en pensent une fois offert.
            </p>
          </div>
          <Sticker
            src={catalogueStickerUrl("cadeau")}
            className="row-start-1 w-28 -rotate-6 sm:col-start-2 sm:w-40"
          />
        </header>

        <Link
          to={GIFT_GUIDE_PAGES.birth.hub}
          className="group flex items-center gap-4 rounded-[2rem] bg-[#eef4fa] p-4 pr-6 transition hover:bg-[#e3edf7] sm:p-5 sm:pr-8"
        >
          <Sticker
            src={catalogueStickerUrl("doudous")}
            className="w-16 shrink-0 -rotate-6 sm:w-20"
          />
          <span className="grid min-w-0 flex-1 gap-0.5">
            <span className="font-display text-2xl font-semibold group-hover:underline">
              {GIFT_GUIDE_PAGES.birth.label}
            </span>
            <span className="text-ink/80">
              Le guide rayon par rayon : sommeil, éveil, repas, balades…
            </span>
          </span>
          <span aria-hidden className="text-2xl font-bold">
            →
          </span>
        </Link>

        <div className="grid gap-4">
          <SearchBox value={text} onChange={setText} suggestions={products.slice(0, 6)} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <OccasionFilter current={occasion} />
            <SortSelect
              value={sort}
              onChange={(next) =>
                void navigate({
                  search: (prev: CatalogueSearch) => ({
                    ...prev,
                    sort: next === "popular" ? undefined : next,
                  }),
                })
              }
            />
          </div>
        </div>

        <CatalogueResults
          catalogue={catalogue}
          emptyTitle={q ? `Rien encore pour « ${q} »` : "Pas encore d'idées ici"}
          emptyAction={
            (q || occasion) && (
              <Link
                to="/idees-cadeaux"
                search={{ sort: sort === "popular" ? undefined : sort }}
                className="btn-secondary"
              >
                Voir toutes les idées
              </Link>
            )
          }
        />

        {categories.data && categories.data.categories.length > 0 && (
          <section className="grid gap-4 pt-8">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <h2 className="text-3xl font-semibold">Idées cadeaux de naissance, par rayon</h2>
              <Link to={GIFT_GUIDE_PAGES.birth.hub} className="font-bold hover:underline">
                Tout le guide naissance
              </Link>
            </div>
            <GuideShelves guide={GIFT_GUIDE_PAGES.birth} categories={categories.data.categories} />
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}

/** Search with suggestions from the current results: picking one opens its page. */
function SearchBox({
  value,
  onChange,
  suggestions,
}: {
  value: string;
  onChange: (value: string) => void;
  suggestions: PublicProduct[];
}) {
  const navigate = useNavigate();
  return (
    <Autocomplete.Root
      items={value.trim().length >= 2 ? suggestions : []}
      value={value}
      onValueChange={(next, details) => {
        if (details.reason !== "item-press") onChange(next);
      }}
      filter={null}
      itemToStringValue={(product: PublicProduct) => product.title}
    >
      <label className="grid gap-1.5">
        <span className="sr-only">Rechercher une idée cadeau</span>
        <Autocomplete.Input
          className="input h-14 text-lg"
          placeholder="Une poussette, un livre en tissu, un Lego…"
          maxLength={LIMITS.searchQueryMax}
        />
      </label>
      <Autocomplete.Portal>
        <Autocomplete.Positioner sideOffset={6} align="start" className="z-40">
          <Autocomplete.Popup className="max-h-80 w-[var(--anchor-width)] overflow-y-auto rounded-2xl border-[1.5px] border-line bg-surface p-1.5 text-ink shadow-xl">
            <Autocomplete.List>
              {(product: PublicProduct) => (
                <Autocomplete.Item
                  key={product.id}
                  value={product}
                  onClick={() => void navigate({ to: "/p/$slug", params: { slug: product.slug } })}
                  className="flex cursor-pointer items-center gap-3 rounded-xl p-2 outline-none select-none data-[highlighted]:bg-ink/[0.07]"
                >
                  <span className="grid size-12 shrink-0 place-items-center overflow-hidden rounded-lg border border-line bg-white">
                    {product.imageUrl && (
                      <img src={product.imageUrl} alt="" className="size-full object-contain" />
                    )}
                  </span>
                  <span className="grid min-w-0">
                    <span className="truncate font-bold">{product.title}</span>
                    {product.rating && (
                      <span className="text-sm text-muted">
                        {formatRating(product.rating.average)}/5, {product.rating.count} avis
                      </span>
                    )}
                  </span>
                </Autocomplete.Item>
              )}
            </Autocomplete.List>
          </Autocomplete.Popup>
        </Autocomplete.Positioner>
      </Autocomplete.Portal>
    </Autocomplete.Root>
  );
}
